import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import "./BookingSections.css";

import flightImg from "../../assets/flight.png";
import trainImg from "../../assets/train.png";
import busImg from "../../assets/bus.png";
import hotelImg from "../../assets/hotel.png";
import { ENABLE_TRAINS } from "../../config/features";

const BookingSections = () => {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({
      duration: 700,
      once: true,
      offset: 50,
      easing: "ease-out-cubic",
      disable: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    });
    AOS.refresh();
  }, []);

  const bookings = [
    {
      id: "flight",
      number: "01",
      title: "Flights",
      description:
        "Take off on your next adventure. Discover domestic and international flight options for your journey.",
      image: flightImg,
      imageAlt: "Airplane representing flight booking",
      features: [
        "Domestic and international routes",
        "Compare available flight options",
        "Review fares before booking",
      ],
      buttonText: "Explore Flights",
      path: "/flights",
    },
    {
      id: "bus",
      number: "02",
      title: "Bus Journeys",
      description:
        "Make every mile count. Explore intercity bus journeys and find options that suit your travel plans.",
      image: busImg,
      imageAlt: "Bus representing road travel",
      features: [
        "Explore available bus options",
        "Review routes and journey details",
        "Compare available travel options",
      ],
      buttonText: "Explore Buses",
      path: "/buses",
    },
    {
      id: "hotel",
      number: "03",
      title: "Hotel Stays",
      description:
        "Find a place to unwind. Explore accommodation options for your next city break or longer getaway.",
      image: hotelImg,
      imageAlt: "Hotel accommodation",
      features: [
        "Explore available stays",
        "Review room and hotel details",
        "Check pricing before booking",
      ],
      buttonText: "Explore Hotels",
      path: "/hotels",
    },
    ...(ENABLE_TRAINS
      ? [
          {
            id: "train",
            number: "04",
            title: "Train Journeys",
            description:
              "Plan your rail journey and explore the train options available for your route.",
            image: trainImg,
            imageAlt: "Train representing rail travel",
            features: [
              "Explore available routes",
              "Review journey details",
              "Check available travel options",
            ],
            buttonText: "Explore Trains",
            path: "/trains",
          },
        ]
      : []),
  ];

  const handleBooking = (path) => {
    const token = localStorage.getItem("mbt_token");
    if (!token) {
      navigate("/profile");
      return;
    }
    navigate(path);
  };

  return (
    <section className="booking-sections" aria-labelledby="booking-title">
      <div className="booking-container">
        <header className="booking-header" data-aos="fade-up">
          <div className="booking-heading-top">
            <span className="booking-heading-line" aria-hidden="true" />
            <span className="booking-eyebrow">TRAVEL YOUR WAY</span>
            <span className="booking-heading-line" aria-hidden="true" />
          </div>

          <h2 id="booking-title" className="booking-title">
            Your next journey<span> starts here.</span>
          </h2>

          <p className="booking-subtitle">
            Whether you're taking off, hitting the road, or checking in, find
            your next travel experience with MakeBeeTrip.
          </p>
        </header>

        <div className="booking-grid">
          {bookings.map((booking, index) => (
            <article
              key={booking.id}
              className={`booking-card booking-card--${booking.id}`}
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className="booking-card__visual">
                <img
                  className="booking-card__image"
                  src={booking.image}
                  alt={booking.imageAlt}
                  loading="lazy"
                />
                <div className="booking-card__image-shade" aria-hidden="true" />
                <span
                  className="booking-card__number"
                  aria-label={`Option ${booking.number}`}
                >
                  {booking.number}
                </span>
              </div>

              <div className="booking-card__content">
                <div className="booking-card__heading">
                  <h3 className="booking-card__title">{booking.title}</h3>
                </div>

                <p className="booking-card__description">{booking.description}</p>

                <ul className="booking-card__features">
                  {booking.features.map((feature) => (
                    <li key={feature}>
                      <span className="booking-card__check" aria-hidden="true">
                        ✓
                      </span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  className="booking-card__btn"
                  onClick={() => handleBooking(booking.path)}
                >
                  {booking.buttonText}
                </button>
              </div>
            </article>
          ))}
        </div>

        <footer className="booking-footer" data-aos="fade-up">
          <span className="booking-footer__symbol" aria-hidden="true">
            ✳
          </span>
          <p>One destination for all your travel plans.</p>
        </footer>
      </div>
    </section>
  );
};

export default BookingSections;
